import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { formatDate, passwordRuleText } from '@brimo/shared';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { ConsentRow, PasswordField, SplitAuthCard, SplitAuthIntro, WizardButton, passwordScore } from '../ui';
import { useAcceptInvitation } from './api';
import type { PublicInvitation } from './types';
import styles from './AcceptInvitation.module.css';

const MISMATCH = 'Die beiden Eingaben stimmen nicht überein.';

type FieldKey = 'password' | 'passwordRepeat' | 'currentPassword' | 'consent';

/** „Stark · mindestens 12 Zeichen, …" — the strength word of the four-bar meter and the rule. */
export function strengthLine(password: string): string {
  const score = passwordScore(password);
  const word = score === 4 ? 'Stark' : score === 3 ? 'Mittel' : 'Schwach';
  return `${word} · ${passwordRuleText}`;
}

/**
 * Onboarding Nutzer 01 / M1: the invited user sets the password (or, with an existing BRIMO account, joins with the
 * current one — own variant) and accepts the notes; the setup session then starts the wizard.
 */
export function AcceptInvitation({ token, invitation }: { token: string; invitation: PublicInvitation }) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const accept = useAcceptInvitation(token);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string | undefined>>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const existing = invitation.accountExists;
  const name = `${invitation.firstName} ${invitation.lastName}`.trim();
  const headline = name ? `Willkommen im Team, ${name}.` : 'Willkommen im Team.';
  const subline = invitation.invitedBy
    ? `${invitation.invitedBy.name} hat Ihnen einen Arbeitsplatz eingerichtet. In etwa zehn Minuten ist alles verbunden.`
    : 'Ihr Arbeitsplatz ist eingerichtet. In etwa zehn Minuten ist alles verbunden.';

  function checkRepeat(value: string) {
    setErrors((before) => ({
      ...before,
      passwordRepeat: value && value !== password ? MISMATCH : undefined,
    }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBanner(null);
    if (!existing && repeat !== password) {
      setErrors((before) => ({ ...before, passwordRepeat: MISMATCH }));
      return;
    }
    try {
      const result = await accept.mutateAsync(
        existing ? { currentPassword: password, consent } : { password, passwordRepeat: repeat, consent },
      );
      await navigate(result.next === 'two_factor' ? '/login/2fa' : '/setup/user/profile', { replace: true });
    } catch (failure) {
      if (failure instanceof ApiError && Object.keys(failure.fields).length > 0) {
        const fields = failure.fields;
        setErrors({
          password: fields['password'],
          passwordRepeat: fields['passwordRepeat'],
          currentPassword: fields['currentPassword'],
          consent: fields['consent'],
        });
        return;
      }
      setBanner(errorMessage(failure));
    }
  }

  const ready = password !== '' && consent && (existing || repeat !== '');
  return (
    <SplitAuthCard eyebrow={invitation.organization.name} headline={headline} subline={subline} width={520}>
      <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <SplitAuthIntro
          eyebrow="Konto aktivieren"
          title={existing ? 'Mit Ihrem bestehenden Passwort beitreten' : 'Passwort festlegen'}
          text={
            existing
              ? `Für ${invitation.email} gibt es bereits ein DAMOKLES-Konto. Bestätigen Sie den Beitritt mit Ihrem Passwort.`
              : mobile
                ? `Anmeldung mit ${invitation.email}`
                : `Ihre Anmeldeadresse ist ${invitation.email}. Sie können sie später nicht selbst ändern.`
          }
        />
        {existing ? (
          <PasswordField
            value={password}
            onChange={(value) => {
              setPassword(value);
              setErrors((before) => ({ ...before, currentPassword: undefined }));
            }}
            error={errors.currentPassword}
            autoComplete="current-password"
            meter={false}
            autoFocus={!mobile}
          />
        ) : (
          <>
            <div className={styles.passwordBlock}>
              <PasswordField
                value={password}
                onChange={(value) => {
                  setPassword(value);
                  setErrors((before) => ({ ...before, password: undefined }));
                }}
                error={errors.password}
                autoFocus={!mobile}
              />
              <span className={styles.strength} aria-hidden="true">
                {strengthLine(password)}
              </span>
            </div>
            <PasswordField
              label="Passwort wiederholen"
              name="passwordRepeat"
              value={repeat}
              meter={false}
              onChange={(value) => {
                setRepeat(value);
                if (errors.passwordRepeat) checkRepeat(value);
              }}
              onBlur={() => checkRepeat(repeat)}
              error={errors.passwordRepeat}
            />
          </>
        )}
        <ConsentRow checked={consent} onChange={setConsent} error={errors.consent}>
          Ich habe die Nutzungsbedingungen und die Datenschutzhinweise gelesen.
        </ConsentRow>
        {banner ? (
          <p className={styles.banner} role="alert">
            {banner}
          </p>
        ) : null}
        <WizardButton
          type="submit"
          variant="primary"
          size={mobile ? 'mobile' : 'large'}
          block
          disabled={!ready}
          loading={accept.isPending}
        >
          {existing ? 'Beitreten' : 'Konto aktivieren'}
        </WizardButton>
        <p className={styles.footer}>
          Einladung gültig bis {formatDate(invitation.validUntil)}
          {invitation.invitedBy ? (
            <>
              {' · Probleme? '}
              <a href={`mailto:${invitation.invitedBy.email}`} className={styles.link}>
                {invitation.invitedBy.email}
              </a>
            </>
          ) : null}
        </p>
      </form>
    </SplitAuthCard>
  );
}
